/**
 * Full-screen, pinch-to-zoom view of a drill diagram.
 *
 * The app turns browser zoom off (maximum-scale=1) so a stray pinch on the
 * field doesn't wreck the layout, which also meant a small drill diagram in the
 * run-mode period sheet couldn't be enlarged at all. This viewer zooms just the
 * diagram: pinch, drag, double-tap, mouse wheel, or the +/− buttons. The SVG is
 * vector, so it stays sharp at any zoom.
 *
 * The maths lives in @coach/core (view/zoom.ts) and is unit tested; this file
 * only turns pointer events into stage coordinates.
 */
import { ZOOM_IDENTITY, panBy, toggleTapZoom, zoomAbout, type ZoomState } from '@coach/core';
import { sanitizeSvgString } from '../../lib/svgImport';
import { useEscapeLayer } from '@coach/ui';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './zoom.css';

const DOUBLE_TAP_MS = 300;
const TAP_SLOP_PX = 10;

export function ZoomView({ svg, title, onClose, extra }: { svg: string; title?: string; onClose: () => void; /** Extra controls for the bottom bar. */ extra?: ReactNode }) {
  useEscapeLayer(true, onClose);
  const stageRef = useRef<HTMLDivElement>(null);
  const [z, setZ] = useState<ZoomState>(ZOOM_IDENTITY);
  const zRef = useRef(z);
  zRef.current = z;

  // Active pointers and the gesture's starting point.
  const pts = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ z: ZoomState; dist: number; mid: { x: number; y: number } } | null>(null);
  const drag = useRef<{ x: number; y: number; moved: number } | null>(null);
  const lastTap = useRef(0);

  const size = () => {
    const r = stageRef.current!.getBoundingClientRect();
    return { W: r.width, H: r.height, left: r.left, top: r.top };
  };
  const local = (e: { clientX: number; clientY: number }) => {
    const { left, top } = size();
    return { x: e.clientX - left, y: e.clientY - top };
  };
  const pinchInfo = () => {
    const [a, b] = [...pts.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
    return { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
  };

  const onDown = (e: React.PointerEvent) => {
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      /* not a live pointer (synthetic event) — capture is only a nicety */
    }
    pts.current.set(e.pointerId, local(e));
    if (pts.current.size === 2) {
      gesture.current = { z: zRef.current, ...pinchInfo() };
      drag.current = null;
    } else if (pts.current.size === 1) {
      const p = local(e);
      drag.current = { x: p.x, y: p.y, moved: 0 };
    }
  };
  const onMove = (e: React.PointerEvent) => {
    if (!pts.current.has(e.pointerId)) return;
    const p = local(e);
    pts.current.set(e.pointerId, p);
    const { W, H } = size();
    if (pts.current.size >= 2 && gesture.current) {
      const g = gesture.current;
      const now = pinchInfo();
      const zoomed = zoomAbout(g.z, now.dist / g.dist, g.mid.x, g.mid.y, W, H);
      setZ(panBy(zoomed, now.mid.x - g.mid.x, now.mid.y - g.mid.y, W, H));
    } else if (drag.current) {
      const d = drag.current;
      d.moved += Math.abs(p.x - d.x) + Math.abs(p.y - d.y);
      setZ((cur) => panBy(cur, p.x - d.x, p.y - d.y, W, H));
      d.x = p.x;
      d.y = p.y;
    }
  };
  const onUp = (e: React.PointerEvent) => {
    const wasDrag = drag.current;
    pts.current.delete(e.pointerId);
    if (pts.current.size < 2) gesture.current = null;
    if (pts.current.size === 1) {
      // One finger lifted from a pinch: carry on panning with the other.
      const [p] = [...pts.current.values()] as [{ x: number; y: number }];
      drag.current = { x: p.x, y: p.y, moved: TAP_SLOP_PX + 1 };
      return;
    }
    drag.current = null;
    if (wasDrag && wasDrag.moved < TAP_SLOP_PX) {
      const now = Date.now();
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        const { W, H } = size();
        const p = local(e);
        setZ((cur) => toggleTapZoom(cur, p.x, p.y, W, H));
        lastTap.current = 0;
      } else lastTap.current = now;
    }
  };

  // Wheel needs a non-passive listener to stop the page scrolling underneath.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { W, H } = size();
      const p = local(e);
      setZ((cur) => zoomAbout(cur, Math.exp(-e.deltaY * 0.0025), p.x, p.y, W, H));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // A different picture (next play in a script) starts unzoomed.
  useEffect(() => setZ(ZOOM_IDENTITY), [svg]);

  // Rotating the phone changes the stage size: start fresh rather than clamp oddly.
  useEffect(() => {
    const reset = () => setZ(ZOOM_IDENTITY);
    window.addEventListener('resize', reset);
    return () => window.removeEventListener('resize', reset);
  }, []);

  const step = useCallback((f: number) => {
    const { W, H } = size();
    setZ((cur) => zoomAbout(cur, f, W / 2, H / 2, W, H));
  }, []);

  return createPortal(
    <div className="zoom-bg" role="dialog" aria-modal="true" aria-label={title ? `${title} diagram` : 'Diagram'}>
      <div className="zoom-top">
        <div className="zoom-title">{title}</div>
        <button className="zoom-x" onClick={onClose} aria-label="Close">✕</button>
      </div>
      <div
        ref={stageRef}
        className="zoom-stage"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <div
          className="zoom-content"
          style={{ transform: `translate(${z.x}px, ${z.y}px) scale(${z.s})` }}
          dangerouslySetInnerHTML={{ __html: sanitizeSvgString(svg) }}
        />
      </div>
      <div className="zoom-bar">
        <button className="btn btn-sm" onClick={() => step(1 / 1.5)} disabled={z.s <= 1.001} aria-label="Zoom out">−</button>
        <button className="btn btn-sm" onClick={() => setZ(ZOOM_IDENTITY)} disabled={z.s <= 1.001}>Fit</button>
        <button className="btn btn-sm" onClick={() => step(1.5)} aria-label="Zoom in">＋</button>
        {extra ?? <span className="muted small">Pinch or double-tap to zoom</span>}
      </div>
    </div>,
    document.body,
  );
}
